/**
 * Pumping programs, the way FMS-Control's Pumping Program manual describes them:
 *
 *   start condition (schedule, a set time, or a reading) ->
 *   start actions (open valves, set pump speed) ->
 *   program steps (each output on for its run time, one after another) ->
 *   stop actions (close the valves again)
 *
 * Stop conditions (over pressure, wind, soil too wet ...) stop the program, or pause it and
 * carry on from the same point when they clear.
 *
 * This file is the decision-making only: given the program, where it's up to and what the
 * devices report, it says what to send next. The server's runner sends it and reports back.
 * Safety rules:
 *  - Run time only counts once the device has CONFIRMED the output is on.
 *  - If a command fails, the program stops and runs its stop actions; it never carries on
 *    guessing what state the equipment is in.
 *  - Device safety rules and timers on the boards stay in charge regardless.
 */

export type OutputKind = "do" | "ro" | "ao";

/** A reading compared with a value; true for `forSec` seconds before it counts. */
export interface RuleCondition {
  devEui: string;
  port: string;
  op: "on" | "off" | ">" | "<" | "between";
  value?: number;
  value2?: number;
  forSec: number;
}

export type StartCondition =
  // `also`: further readings that must all hold too ("tank below 180 cm AND its valve selected"),
  // as FMS-Control's control rules with extra conditions.
  | ({ type: "rule"; also?: RuleCondition[] } & RuleCondition)
  | { type: "schedule"; days: number[]; time: string } // days 0 = Sunday; time "HH:MM" local
  | { type: "once"; at: number };

export interface StopCondition extends RuleCondition {
  /** Pause (carry on from the same point when it clears) instead of stopping. */
  pause: boolean;
}

/** Switch an output (state) or set an analog output (value), after waiting `waitSec`. */
export interface Action {
  devEui: string;
  kind: OutputKind;
  channel: number;
  /** A Milesight controller's output by its channel name ("do_1", "valve_2"); kind/channel are unused then. */
  port?: string;
  state?: boolean;
  value?: number;
  waitSec: number;
}

/**
 * One output on for `runMin` minutes, starting `waitSec` after the steps begin (after the start
 * actions), as in FMS-Control: steps run on their own times and may overlap (the next valve
 * opening a minute before the last one closes) or start together. Run time counts from when
 * the device confirms the output is on.
 */
export interface Step {
  devEui: string;
  kind: "do" | "ro";
  channel: number;
  /** A Milesight controller's output by its channel name, as on Action. */
  port?: string;
  waitSec: number;
  runMin: number;
  /** Stays on until the program is stopped (a stop condition or a person), as FMS-Control's "run indefinitely". */
  untilStop?: boolean;
}

export interface Program {
  id: string;
  accountId: string;
  name: string;
  enabled: boolean;
  timezone: string;
  start: StartCondition[];
  startActions: Action[];
  steps: Step[];
  stopConditions: StopCondition[];
  stopActions: Action[];
  /** Why it's set up the way it is, e.g. what an imported FMS-Control program still needs. */
  note?: string;
  /** Switch itself off once it has run (a rule pairing pumps with an irrigator for one day). */
  offAfterRun?: boolean;
  updatedAt: number;
  updatedBy: string;
}

export type RunState = "idle" | "running" | "pausing" | "paused" | "stopping" | "finished" | "stopped" | "failed";
type Phase = "start" | "steps" | "stop";

export interface Run {
  programId: string;
  state: RunState;
  phase?: Phase;
  /** Index into the phase's list. */
  index: number;
  /** Don't do the next thing before this time (the action's or step's wait). */
  notBefore?: number;
  /** Step i: confirmed on at this time (run time counts from here). */
  stepOnAt?: number;
  /** Run time already done on step i before a pause. */
  stepDoneMs: number;
  /** Commands sent and not yet confirmed; the runner fills in the ids. `step`: which step it's for. */
  waiting?: { purpose: "action" | "step-on" | "step-off"; ids: string[]; since: number; step?: number };
  /** Why it's stopping/stopped/paused/failed, in plain words. */
  reason?: string;
  /** What happens once the stop actions are done. */
  after?: "finished" | "stopped" | "paused" | "failed";
  startedAt?: number;
  /** When each rule (start or stop condition, by key) first became true. */
  trueSince: Record<string, number>;
  /** Rule starts fire once, then wait for the reading to go back before firing again. */
  armed: boolean;
  /** Schedule/once already fired for this minute ("2026-10-25 05:00"). */
  lastTrigger?: string;
  log: Array<{ at: number; text: string }>;
  /** Winding down: switch the step output off before the stop actions. */
  offFirst?: boolean;
  /** Winding down: which stop action is next. */
  stopIndex?: number;
  /** Resuming after a pause: after the start actions, carry on at `pausedStep`. */
  resumeStep?: boolean;
  pausedStep?: number;
  /** Each step's own progress in the steps phase (steps may overlap). */
  steps?: StepRun[];
  /** The steps' clock: time already counted before `clockFrom`, and when it last started (unset while paused or stopping). */
  clockMs?: number;
  clockFrom?: number;
  /** Winding down: the steps whose outputs are (or may be) on, to switch off before the stop actions. */
  offSteps?: number[];
}

export interface StepRun {
  state: "waiting" | "on" | "done";
  /** Confirmed on at this time. */
  onAt?: number;
  /** Run time done before a pause. */
  doneMs: number;
  /** Was on when the program paused: on again as soon as it resumes. */
  resume?: boolean;
  /** This step's command, sent and not yet confirmed (steps don't wait on each other's). */
  cmd?: { on: boolean; ids: string[]; since: number };
}

export interface Env {
  now: number;
  reading(devEui: string, port: string): { value: number; at: number } | undefined;
  /**
   * When anything was last heard from the device (any message, heartbeats included). FMS v1
   * boards only report an input when it changes, so a port's own reading can be hours old on a
   * board that is talking fine. Falls back to the port's reading when not given.
   */
  heard?(devEui: string): number | undefined;
  /** Status of a command the runner sent ("queued", "sent", "confirmed", "unconfirmed", "failed", "cancelled"). */
  command(id: string): string | undefined;
  /** Device name and output label for the log. */
  label(devEui: string, port: string): string;
}

export interface Send {
  /** A step's command: its id goes on that step (see attachCommand). */
  step?: number;
  devEui: string;
  kind: OutputKind;
  channel: number;
  /** Set for a Milesight controller's output (see Action.port). */
  port?: string;
  state?: boolean;
  value?: number;
}

export interface Advance {
  run: Run;
  sends: Send[];
}

/**
 * Where a sent command's id goes: the step it's for, else the run's own wait. The runners call
 * this for each send, with the queue's id for it.
 */
export function attachCommand(run: Run, send: Send, id: string): void {
  const cmd = send.step !== undefined ? run.steps?.[send.step]?.cmd : undefined;
  if (cmd) cmd.ids.push(id);
  else run.waiting?.ids.push(id);
}

/**
 * When the next zone's "on" is still unconfirmed as the last one's time is up, its "off" waits
 * this long for it, so the pump isn't left pushing against every valve shut; then goes anyway,
 * so a slow or offline device can't keep another zone running on (a Class A valve can take an
 * hour to answer).
 */
export const OFF_WAITS_FOR_ON_MS = 2 * 60_000;

/** A command that hasn't been confirmed in this long is treated as failed. */
export const CONFIRM_TIMEOUT_MS = 10 * 60_000;
/**
 * While the command queue is still trying (queued / sent), its own verdict is waited for, up to
 * this long: a Class A valve (UC51x) is retried 3 × 20 min. Giving up first left the queue
 * re-sending OPEN after the program had stopped, so the valve could open with nothing running.
 */
export const QUEUE_LIMIT_MS = 70 * 60_000;
const READING_STALE_MS = 30 * 60_000;
/**
 * A device a stop condition watches that hasn't been heard for this long can't stop anything:
 * the program is running blind (a tank that could overflow), so it stops, outputs off, and a
 * person starts it again once the device is back. Never an automatic restart: the device could
 * come back days later. Two hours so a battery sensor on a 60 min interval can miss a report.
 */
export const DEVICE_QUIET_MS = 2 * 3600_000;

/** Nothing heard from the condition's device for DEVICE_QUIET_MS (or ever). */
function deviceQuiet(c: RuleCondition, env: Env): number | false {
  const at = env.heard ? env.heard(c.devEui) : env.reading(c.devEui, c.port)?.at;
  if (at === undefined) return Infinity;
  return env.now - at > DEVICE_QUIET_MS ? env.now - at : false;
}

/** The condition on its last reported value, however old (a switch that reported "on" is still on). */
function lastValueTrue(c: RuleCondition, env: Env): boolean {
  const r = env.reading(c.devEui, c.port);
  return r !== undefined && conditionTrue(c, r, r.at);
}

function quietText(c: RuleCondition, env: Env, ms: number): string {
  return `nothing heard from ${env.label(c.devEui, c.port)} ${Number.isFinite(ms) ? `for ${Math.round(ms / 60_000)} min` : "yet"}`;
}

export function newRun(programId: string): Run {
  return { programId, state: "idle", index: 0, stepDoneMs: 0, trueSince: {}, armed: true, log: [] };
}

export const isActive = (s: RunState) => s === "running" || s === "pausing" || s === "stopping";

// ------------------------------------------------------------------ conditions

/** Is the condition true right now (ignoring how long)? Readings older than 30 min don't count. */
export function conditionTrue(c: RuleCondition, r: { value: number; at: number } | undefined, now: number): boolean {
  if (!r || now - r.at > READING_STALE_MS) return false;
  switch (c.op) {
    case "on":
      return r.value !== 0;
    case "off":
      return r.value === 0;
    case ">":
      return c.value !== undefined && r.value > c.value;
    case "<":
      return c.value !== undefined && r.value < c.value;
    case "between":
      return c.value !== undefined && c.value2 !== undefined && r.value >= Math.min(c.value, c.value2) && r.value <= Math.max(c.value, c.value2);
  }
}

/** True once the condition has held for its period; keeps track of since-when in `run.trueSince`. */
function held(run: Run, key: string, c: RuleCondition, env: Env): boolean {
  if (!conditionTrue(c, env.reading(c.devEui, c.port), env.now)) {
    delete run.trueSince[key];
    return false;
  }
  run.trueSince[key] ??= env.now;
  return env.now - run.trueSince[key] >= c.forSec * 1000;
}

/** Local day of week, date and "HH:MM" for schedules. */
export function localParts(now: number, timeZone: string): { day: number; date: string; time: string } {
  const f = new Intl.DateTimeFormat("en-GB", { timeZone, weekday: "short", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const p = Object.fromEntries(f.formatToParts(new Date(now)).map((x) => [x.type, x.value]));
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return { day: days.indexOf(p.weekday ?? "Sun"), date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

function startTriggered(p: Program, run: Run, env: Env): string | null {
  const local = localParts(env.now, p.timezone);
  const minute = `${local.date} ${local.time}`;
  let anyRuleTrue = false;
  // A rule whose readings have gone stale (no report for 30 min) is neither true nor false:
  // it mustn't re-arm, or the next report saying the same thing would start it again. Boards
  // that only report now and then did exactly that (a "pump off when the irrigator stops" rule
  // firing ~40 times a day, or turning a bore back on after FMS-Control had stopped it).
  let anyRuleUnknown = false;
  for (const [i, c] of p.start.entries()) {
    if (c.type === "schedule") {
      if (c.days.includes(local.day) && c.time === local.time && run.lastTrigger !== minute) {
        run.lastTrigger = minute;
        return `schedule ${c.time}`;
      }
    } else if (c.type === "once") {
      const key = `once ${c.at}`;
      if (env.now >= c.at && env.now - c.at < 5 * 60_000 && run.lastTrigger !== key) {
        run.lastTrigger = key;
        return "set time";
      }
    } else {
      const also = c.also ?? [];
      // Every part is checked each time so each one's "held for" clock keeps running.
      const parts = [held(run, `start${i}`, c, env), ...also.map((a, j) => held(run, `start${i}.${j}`, a, env))];
      const ok = parts.every(Boolean);
      if ([c, ...also].every((x) => conditionTrue(x, env.reading(x.devEui, x.port), env.now))) anyRuleTrue = true;
      else if (!readingFalse(c, also, env)) anyRuleUnknown = true;
      if (ok && run.armed) {
        run.armed = false;
        return [c, ...also].map((x) => `${env.label(x.devEui, x.port)} ${describeRule(x)}`).join(" and ");
      }
    }
  }
  // Re-arm rule starts once fresh readings show none of them is true any more.
  if (!anyRuleTrue && !anyRuleUnknown) run.armed = true;
  return null;
}

/** A fresh reading shows the rule false: some part of it is reported and not true. */
function readingFalse(c: RuleCondition, also: RuleCondition[], env: Env): boolean {
  return [c, ...also].some((x) => {
    const r = env.reading(x.devEui, x.port);
    return r !== undefined && env.now - r.at <= READING_STALE_MS && !conditionTrue(x, r, env.now);
  });
}

export function describeRule(c: RuleCondition): string {
  const t = c.op === "on" ? "is on" : c.op === "off" ? "is off" : c.op === "between" ? `is between ${c.value} and ${c.value2}` : `${c.op === ">" ? "is above" : "is below"} ${c.value}`;
  return c.forSec ? `${t} for ${c.forSec} s` : t;
}

// ------------------------------------------------------------------ manual control

export type ManualAction = "start" | "pause" | "resume" | "stop" | "reset";

/** What a person pressed. Returns an error message if it doesn't apply in this state. */
export function manual(p: Program, run: Run, action: ManualAction, who: string, now: number): { run: Run; error?: string } {
  const r = structuredClone(run);
  switch (action) {
    case "start":
      if (isActive(r.state) || r.state === "paused") return { run, error: "It's already running or paused." };
      if (!p.steps.length && !p.startActions.length) return { run, error: "Add at least one step first." };
      begin(r, p, now, `started by ${who}`);
      return { run: r };
    case "pause":
      if (r.state !== "running") return { run, error: "Only a running program can be paused." };
      wind(r, p, now, "paused", `paused by ${who}`);
      return { run: r };
    case "resume":
      if (r.state !== "paused") return { run, error: "It isn't paused." };
      resume(r, now, `resumed by ${who}`);
      return { run: r };
    case "stop":
      if (!isActive(r.state) && r.state !== "paused") return { run, error: "It isn't running." };
      if (r.state === "paused") {
        log(r, now, `stopped by ${who}`);
        finish(r, "stopped", `stopped by ${who}`);
        return { run: r };
      }
      wind(r, p, now, "stopped", `stopped by ${who}`);
      return { run: r };
    case "reset":
      if (isActive(r.state)) return { run, error: "Stop it first." };
      return { run: { ...newRun(p.id), log: [...r.log, { at: now, text: `reset by ${who}` }].slice(-200) } };
  }
}

function log(r: Run, at: number, text: string) {
  r.log = [...r.log, { at, text }].slice(-200);
}

function begin(r: Run, p: Program, now: number, why: string) {
  r.state = "running";
  r.phase = "start";
  r.index = 0;
  r.stepDoneMs = 0;
  delete r.stepOnAt;
  delete r.waiting;
  delete r.reason;
  delete r.after;
  r.startedAt = now;
  r.notBefore = now + (p.startActions[0]?.waitSec ?? 0) * 1000;
  delete r.steps;
  delete r.clockMs;
  delete r.clockFrom;
  delete r.offSteps;
  log(r, now, `Started: ${why}`);
}

function resume(r: Run, now: number, why: string) {
  // Start actions run again (valves may have been closed by the pause), then the same step
  // carries on with the run time it had left.
  r.state = "running";
  r.phase = "start";
  r.index = 0;
  r.notBefore = now;
  delete r.reason;
  delete r.after;
  r.resumeStep = true;
  log(r, now, `Resumed: ${why}`);
}

/** Switch off whatever step is on, then run the stop actions, then end up in `after`. */
function wind(r: Run, p: Program, now: number, after: NonNullable<Run["after"]>, why: string) {
  r.state = after === "paused" ? "pausing" : "stopping";
  r.after = after;
  r.reason = why;
  log(r, now, why.charAt(0).toUpperCase() + why.slice(1));
  // Every step output that is (or may be) on is switched off first; the clock stops, so a
  // pause carries on from the same point.
  if (r.phase === "steps") {
    const steps = stepRuns(r, p, now);
    r.clockMs = clock(r, now);
    delete r.clockFrom;
    const off: number[] = [];
    for (const [i, st] of steps.entries()) {
      // On, or with a command still in flight (it may land): switched off, then.
      if (st.state === "on" || st.cmd) {
        if (st.state === "on" && st.onAt !== undefined) st.doneMs += now - st.onAt;
        delete st.onAt;
        // On again when a pause ends (one that was turning off has done its time).
        const turningOff = st.cmd !== undefined && !st.cmd.on;
        delete st.cmd;
        if (turningOff) markDone(st, p.steps[i]);
        else (st.state = "waiting"), (st.resume = true);
        off.push(i);
      }
    }
    r.offSteps = off;
    r.index = off[0] ?? currentStep(steps);
    delete r.stepOnAt;
    delete r.offFirst;
  }
  delete r.waiting;
  r.phase = "stop";
  r.stopIndex = 0;
  r.notBefore = now;
  void p;
}

/**
 * Wind a run down from outside the tick (the server restarted mid-run, or the farm was handed
 * over): every step output that may be on is switched off, then the stop actions run.
 */
export function stopRun(p: Program, run: Run, now: number, why: string): Run {
  const r = structuredClone(run);
  if (r.phase !== "steps" && r.phase !== "stop") delete r.waiting;
  wind(r, p, now, "stopped", why);
  return r;
}

function finish(r: Run, state: RunState, reason?: string) {
  r.state = state;
  delete r.phase;
  delete r.waiting;
  delete r.notBefore;
  delete r.stepOnAt;
  delete r.offFirst;
  delete r.stopIndex;
  delete r.offSteps;
  if (state !== "paused") {
    r.index = 0;
    r.stepDoneMs = 0;
    delete r.resumeStep;
    delete r.steps;
    delete r.clockMs;
    delete r.clockFrom;
  }
  if (reason) r.reason = reason;
  delete r.after;
}


// ------------------------------------------------------------------ the tick

export function advance(p: Program, run: Run, env: Env): Advance {
  const r = structuredClone(run);
  const sends: Send[] = [];
  const now = env.now;

  // A run saved by the engine before steps had their own commands: move its step command there.
  if (r.phase === "steps") stepRuns(r, p, now);

  // 1. Waiting on commands (start and stop actions, switching off on the way out): carry on
  // only when every one is confirmed. Steps' own commands are looked at with the steps (4).
  if (r.waiting) {
    const v = verdict(r.waiting.ids, r.waiting.since, env);
    const failed = v === "failed";
    const done = v === "done";
    if (failed) {
      const what = r.waiting.purpose;
      const wstep = r.waiting.step ?? r.index;
      delete r.waiting;
      if (r.phase === "stop") {
        // Best effort while winding down: note it and keep going.
        log(r, now, "A stop action wasn't confirmed by the device. Check it on site.");
        if (what === "step-on" || what === "step-off") {
          r.offSteps = (r.offSteps ?? []).slice(1);
          delete r.offFirst;
        } else r.stopIndex = (r.stopIndex ?? 0) + 1;
        r.after = "failed";
        r.reason = "A device didn't confirm switching off. Check on site.";
      } else {
        const why = "a device didn't confirm a start action";
        log(r, now, `Problem: ${why}.`);
        void wstep;
        wind(r, p, now, "failed", why);
      }
      return { run: r, sends };
    }
    if (!done) return checkStops(p, r, env, sends);
    const purpose = r.waiting.purpose;
    const wstep = r.waiting.step ?? r.index;
    delete r.waiting;
    if (purpose === "step-on" || purpose === "step-off") {
      const i = wstep;
      const s = p.steps[i];
      const name = s ? env.label(s.devEui, outKey(s)) : `step ${i + 1}`;
      // Only switching off on the way out comes here (steps' own commands are on the steps).
      r.offSteps = (r.offSteps ?? []).filter((x) => x !== i);
      delete r.offFirst;
      log(r, now, `${name} off (confirmed)`);
    } else if (r.phase === "stop") {
      r.stopIndex = (r.stopIndex ?? 0) + 1;
      r.notBefore = now + (p.stopActions[r.stopIndex]?.waitSec ?? 0) * 1000;
    } else {
      r.index++;
      r.notBefore = now + (p.startActions[r.index]?.waitSec ?? 0) * 1000;
    }
  }

  // 2. Idle or ended: should it start?
  if (r.state === "idle" || r.state === "finished" || r.state === "stopped" || r.state === "failed") {
    // A program with only start actions is a farm rule ("when the tank is low, open the fill valve").
    if (!p.enabled || (!p.steps.length && !p.startActions.length)) return { run: r, sends };
    const why = startTriggered(p, r, env);
    if (why) begin(r, p, now, why);
    else return { run: r, sends };
  }

  // 3. Paused by a stop condition: carry on once every pausing condition has cleared.
  if (r.state === "paused") {
    // Only pauses caused by a stop condition resume by themselves; a person's pause waits for a person.
    if (r.reason?.startsWith("paused:")) {
      // The device it waits on has gone quiet: it can't tell us the condition has cleared, and
      // when it comes back it may be days later. End it; a person starts it again.
      for (const c of p.stopConditions) {
        const quiet = deviceQuiet(c, env);
        if (quiet !== false) {
          const why = `stopped: ${quietText(c, env, quiet)} while paused. Start it again once it's reporting`;
          log(r, now, why.charAt(0).toUpperCase() + why.slice(1));
          finish(r, "stopped", why);
          return { run: r, sends };
        }
      }
    }
    // Cleared means the last report says so, however old: a switch only reports when it changes,
    // and an old "above 10" mustn't count as "not above" just because it's old (it used to).
    const blocking = p.stopConditions.some((c) => c.pause && lastValueTrue(c, env));
    if (!blocking && r.reason?.startsWith("paused:")) {
      for (const k of Object.keys(r.trueSince)) if (k.startsWith("stop")) delete r.trueSince[k];
      resume(r, now, "the stop condition cleared");
    }
    return { run: r, sends };
  }

  if (r.state === "running") {
    const stopped = checkStops(p, r, env, sends);
    if (stopped.run.state !== "running") return stopped;
  }
  if (r.notBefore !== undefined && now < r.notBefore) return { run: r, sends };

  // 4. Do the next thing in the current phase.
  if (r.phase === "start") {
    const a = p.startActions[r.index];
    if (a) return sendAction(r, a, now, sends, "action");
    r.phase = "steps";
    if (r.resumeStep && (r.steps || p.steps.length)) {
      // A run paused by the engine before steps could overlap: carry on at the step it paused
      // on (resume() set index back to 0 for the start actions).
      if (!r.steps) r.index = r.pausedStep ?? r.index;
      stepRuns(r, p, now);
      // Carry on where it paused: the clock runs again, the steps that were on come back on
      // with the run time they had left.
      delete r.resumeStep;
      delete r.pausedStep;
    } else {
      delete r.resumeStep;
      delete r.pausedStep;
      r.steps = p.steps.map(() => ({ state: "waiting", doneMs: 0 }));
      r.clockMs = 0;
      r.stepDoneMs = 0;
    }
    r.clockFrom = now;
    r.notBefore = now;
    syncIndex(r, p, now);
    return { run: r, sends };
  }

  if (r.phase === "steps") {
    const steps = stepRuns(r, p, now);
    // Each step's own command: confirmed, it's on (run time counts from now) or done; not
    // confirmed, the program stops with every output off.
    for (const [i, st] of steps.entries()) {
      if (!st.cmd) continue;
      const v = verdict(st.cmd.ids, st.cmd.since, env);
      if (v === "wait") continue;
      const s = p.steps[i];
      const name = s ? env.label(s.devEui, outKey(s)) : `step ${i + 1}`;
      if (v === "failed") {
        const why = st.cmd.on ? "the device didn't confirm the output turned on" : "the device didn't confirm the output turned off";
        log(r, now, `Problem: ${why}.`);
        wind(r, p, now, "failed", why); // the output may or may not be on: switched off again
        return { run: r, sends };
      }
      const on = st.cmd.on;
      delete st.cmd;
      if (on) {
        st.state = "on";
        st.onAt = now;
        delete st.resume;
        log(r, now, `${name} on (confirmed)`);
      } else {
        markDone(st, s);
        log(r, now, `${name} off (confirmed), step ${i + 1} done`);
      }
    }
    syncIndex(r, p, now);
    // Then the next thing that is due, one command a tick, in time order. When an output is due
    // on and another due off at the same moment, on goes first; an off waits a little for an
    // "on" still in flight (OFF_WAITS_FOR_ON_MS), so a pump never sees every valve shut between zones.
    const el = clock(r, now);
    const onInFlight = steps.some((o) => o.cmd?.on && now - o.cmd.since < OFF_WAITS_FOR_ON_MS);
    const busy = (s: Step) => steps.some((o, j) => o.cmd && p.steps[j] && p.steps[j]!.devEui === s.devEui && outKey(p.steps[j]!) === outKey(s));
    let next: { i: number; on: boolean; due: number } | undefined;
    for (const [i, st] of steps.entries()) {
      const s = p.steps[i];
      if (!s || st.cmd || busy(s)) continue; // one command at a time per output, in order
      let due: number | undefined;
      let on = false;
      if (st.state === "waiting") {
        const at = st.resume ? -Infinity : s.waitSec * 1000;
        if (at <= el) (due = at), (on = true);
      } else if (st.state === "on" && !s.untilStop && st.onAt !== undefined) {
        const offAt = st.onAt + s.runMin * 60_000 - st.doneMs;
        if (now >= offAt && !onInFlight) due = el - (now - offAt);
      }
      if (due === undefined) continue;
      if (!next || due < next.due || (due === next.due && on && !next.on)) next = { i, on, due };
    }
    if (!next) {
      if (steps.every((st) => st.state === "done" && !st.cmd)) wind(r, p, now, "finished", "finished");
      return { run: r, sends };
    }
    const s = p.steps[next.i]!;
    const st = steps[next.i]!;
    // Two steps on the same output: it stays on while either is on.
    const shared = steps.some((o, j) => j !== next!.i && o.state === "on" && p.steps[j] && outKey(p.steps[j]!) === outKey(s) && p.steps[j]!.devEui === s.devEui);
    if (next.on) {
      if (shared) {
        st.state = "on";
        st.onAt = now;
        delete st.resume;
        syncIndex(r, p, now);
        return { run: r, sends };
      }
      sends.push({ step: next.i, devEui: s.devEui, kind: s.kind, channel: s.channel, ...(s.port ? { port: s.port } : {}), state: true });
      st.cmd = { on: true, ids: [], since: now };
      return { run: r, sends };
    }
    if (shared) {
      markDone(st, s);
      syncIndex(r, p, now);
      return { run: r, sends };
    }
    sends.push({ step: next.i, devEui: s.devEui, kind: s.kind, channel: s.channel, ...(s.port ? { port: s.port } : {}), state: false });
    st.cmd = { on: false, ids: [], since: now };
    return { run: r, sends };
  }

  if (r.phase === "stop") {
    // Switch off every step output that was on first (one per tick, each confirmed).
    if (r.offFirst && !r.offSteps) r.offSteps = [r.index]; // a run saved before steps could overlap
    const seen = new Set<string>();
    r.offSteps = (r.offSteps ?? []).filter((i) => {
      const s = p.steps[i];
      if (!s) return false;
      const k = `${s.devEui}:${outKey(s)}`;
      if (seen.has(k)) return false; // the same output, already on the list
      seen.add(k);
      return true;
    });
    const offNext = r.offSteps[0];
    if (offNext !== undefined) {
      const s = p.steps[offNext]!;
      sends.push({ devEui: s.devEui, kind: s.kind, channel: s.channel, ...(s.port ? { port: s.port } : {}), state: false });
      r.waiting = { purpose: "step-off", ids: [], since: now, step: offNext };
      return { run: r, sends };
    }
    delete r.offFirst;
    const i = r.stopIndex ?? 0;
    const a = p.stopActions[i];
    if (a) {
      if (r.notBefore !== undefined && now < r.notBefore) return { run: r, sends };
      return sendAction(r, a, now, sends, "action");
    }
    const after = r.after ?? "stopped";
    if (after === "paused") r.pausedStep = r.index;
    log(r, now, after === "finished" ? "Finished" : after === "paused" ? "Paused" : after === "failed" ? "Stopped because of a problem" : "Stopped");
    const reason = r.reason;
    finish(r, after, after === "finished" ? undefined : reason);
    if (after === "finished") delete r.reason;
    return { run: r, sends };
  }
  return { run: r, sends };
}

function sendAction(r: Run, a: Action, now: number, sends: Send[], purpose: "action"): Advance {
  sends.push({ devEui: a.devEui, kind: a.kind, channel: a.channel, ...(a.port ? { port: a.port } : {}), ...(a.kind === "ao" ? { value: a.value ?? 0 } : { state: a.state ?? false }) });
  r.waiting = { purpose, ids: [], since: now };
  return { run: r, sends };
}

/** Running: has a stop condition held for its period? Then pause or stop. */
function checkStops(p: Program, r: Run, env: Env, sends: Send[]): Advance {
  if (r.state !== "running") return { run: r, sends };
  for (const [i, c] of p.stopConditions.entries()) {
    if (held(r, `stop${i}`, c, env)) {
      const what = `${env.label(c.devEui, c.port)} ${describeRule(c)}`;
      // A command in flight may still land: wind() switches the step off regardless.
      wind(r, p, env.now, c.pause ? "paused" : "stopped", c.pause ? `paused: ${what}` : `stopped: ${what}`);
      return { run: r, sends };
    }
  }
  // A stop condition on a device that has gone quiet can't stop it: stop (outputs off).
  for (const c of p.stopConditions) {
    const quiet = deviceQuiet(c, env);
    if (quiet !== false) {
      wind(r, p, env.now, "stopped", `stopped: ${quietText(c, env, quiet)}, so its stop condition couldn't be checked. Start it again once it's reporting`);
      return { run: r, sends };
    }
  }
  return { run: r, sends };
}


/** How long the steps take from the first starting to the last finishing, in minutes (steps run until stopped count as 0). */
export function stepsLength(p: Pick<Program, "steps">): number {
  return p.steps.reduce((n, s) => Math.max(n, s.waitSec / 60 + (s.untilStop ? 0 : s.runMin)), 0);
}

/** Share of the steps' time done, 0-100. */
export function progress(p: Program, r: Run, now: number): number {
  const total = stepsLength(p) * 60_000;
  if (!total) return 0;
  if (r.state === "finished") return 100;
  if (r.state === "idle") return 0;
  if (!r.steps) return 0;
  return Math.max(0, Math.min(100, Math.round((100 * clock(r, now)) / total)));
}

// ------------------------------------------------------------------ the steps' own clocks

const outKey = (s: Pick<Step, "kind" | "channel" | "port">) => s.port ?? `${s.kind}${s.channel}`;

/** Time on the steps' clock (pauses don't count). */
function clock(r: Run, now: number): number {
  return (r.clockMs ?? 0) + (r.clockFrom !== undefined ? now - r.clockFrom : 0);
}

function markDone(st: StepRun, s: Step | undefined) {
  st.state = "done";
  st.doneMs = (s?.runMin ?? 0) * 60_000;
  delete st.onAt;
  delete st.resume;
}

/**
 * Each step's progress, made on first use. A run saved before steps could overlap (one step at
 * a time, `index` / `stepOnAt`) carries on from where it was: earlier steps done, this one on
 * with its time so far, the clock set as if this step started on time.
 */
function stepRuns(r: Run, p: Program, now: number): StepRun[] {
  if (!r.steps) {
    r.steps = p.steps.map((_, i) => ({ state: i < r.index ? "done" : "waiting", doneMs: i < r.index ? (p.steps[i]?.runMin ?? 0) * 60_000 : 0 }));
    const cur = r.steps[r.index];
    if (cur && r.phase === "steps") {
      cur.doneMs = r.stepDoneMs;
      if (r.stepOnAt !== undefined) (cur.state = "on"), (cur.onAt = r.stepOnAt);
      r.clockMs = (p.steps[r.index]?.waitSec ?? 0) * 1000 + r.stepDoneMs;
      r.clockFrom = r.stepOnAt ?? now;
      // Its step command in flight becomes the step's own.
      if (r.waiting && (r.waiting.purpose === "step-on" || r.waiting.purpose === "step-off")) {
        cur.cmd = { on: r.waiting.purpose === "step-on", ids: r.waiting.ids, since: r.waiting.since };
        delete r.waiting;
      }
    } else if (cur) {
      // Paused, or winding down to a pause: this step carries on where it was when resumed
      // (on straight away if it had started), the clock as if it had started on time.
      cur.doneMs = r.stepDoneMs;
      cur.resume = r.stepDoneMs > 0 || r.offFirst === true;
      r.clockMs = (p.steps[r.index]?.waitSec ?? 0) * 1000 + r.stepDoneMs;
    }
    delete r.stepOnAt;
  }
  while (r.steps.length < p.steps.length) r.steps.push({ state: "waiting", doneMs: 0 });
  return r.steps;
}

/** The step to show as "current": the first one on, else the next one due. */
function currentStep(steps: StepRun[]): number {
  const on = steps.findIndex((x) => x.state === "on");
  if (on >= 0) return on;
  const wait = steps.findIndex((x) => x.state === "waiting");
  return wait >= 0 ? wait : Math.max(0, steps.length - 1);
}

/** All of a command's ids confirmed ("done"), one failed or too long ("failed"), else "wait". */
function verdict(ids: string[], since: number, env: Env): "done" | "failed" | "wait" {
  const statuses = ids.map((id) => env.command(id));
  const waited = env.now - since;
  // A status nobody knows any more (lost) fails at CONFIRM_TIMEOUT_MS; one the queue is still
  // working on waits for the queue to confirm or fail it, with QUEUE_LIMIT_MS as a backstop.
  if (
    statuses.some((s) => s === "failed" || s === "cancelled") ||
    (ids.length > 0 && waited > CONFIRM_TIMEOUT_MS && statuses.some((s) => s === undefined)) ||
    (ids.length > 0 && waited > QUEUE_LIMIT_MS)
  ) return "failed";
  return ids.length > 0 && statuses.every((s) => s === "confirmed" || s === "unconfirmed") ? "done" : "wait";
}

function syncIndex(r: Run, p: Program, now: number) {
  const steps = stepRuns(r, p, now);
  r.index = currentStep(steps);
  const st = steps[r.index];
  r.stepDoneMs = st?.doneMs ?? 0;
}

// ------------------------------------------------------------------ checking what was entered

export interface ProgramDevice {
  devEui: string;
  name: string;
  model: string;
}

const isHHMM = (s: unknown) => typeof s === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
const num = (v: unknown) => typeof v === "number" && Number.isFinite(v);

/**
 * Checks a program from the app before it's saved. Outputs must be on FMS boards on the same
 * account (only they confirm commands); readings can be on any device of the account.
 * Returns a plain-English problem, or null.
 */
/**
 * `milesightOutput`: whether a non-FMS device has this switchable output (the server passes
 * the drivers' list: UC300 do_1, UC50x gpio_out_1, UC51x valve_1 …). Without it, only FMS
 * board outputs are accepted.
 */
export function programProblem(
  p: Omit<Program, "id" | "accountId" | "updatedAt" | "updatedBy">,
  devices: readonly ProgramDevice[],
  milesightOutput?: (model: string, port: string) => boolean,
): string | null {
  const byEui = new Map(devices.map((d) => [d.devEui, d]));
  if (typeof p.name !== "string" || !p.name.trim()) return "Give the program a name.";
  if (!Array.isArray(p.start) || !Array.isArray(p.startActions) || !Array.isArray(p.steps) || !Array.isArray(p.stopConditions) || !Array.isArray(p.stopActions)) {
    return "The program is incomplete.";
  }
  if (!p.steps.length && !p.startActions.length) return "Add at least one step (an output and how long it runs), or a start action.";
  /** null when fine; "milesight" when it's a controller output named by `port`. */
  const board = (eui: string, what: string, port?: string): string | null | "milesight" => {
    const d = byEui.get(eui);
    if (!d) return `${what}: choose a device on this account.`;
    if (d.model === "multipro" || d.model === "mini") return null;
    if (port && milesightOutput?.(d.model, port)) return "milesight";
    if (milesightOutput) return `${what}: choose one of ${d.name}'s outputs.`;
    return `${what}: ${d.name} can't be switched by programs yet (only FMS boards confirm commands).`;
  };
  const rule = (c: RuleCondition, what: string) => {
    if (!byEui.has(c.devEui)) return `${what}: choose a device on this account.`;
    if (typeof c.port !== "string" || !c.port) return `${what}: choose a reading.`;
    if (!["on", "off", ">", "<", "between"].includes(c.op)) return `${what}: choose a condition.`;
    if ((c.op === ">" || c.op === "<" || c.op === "between") && !num(c.value)) return `${what}: enter a value.`;
    if (c.op === "between" && !num(c.value2)) return `${what}: enter both values.`;
    if (!num(c.forSec) || c.forSec < 0 || c.forSec > 86_400) return `${what}: the time it must hold is 0 to 86400 seconds.`;
    return null;
  };
  for (const [i, c] of p.start.entries()) {
    const what = `Start condition ${i + 1}`;
    if (c.type === "schedule") {
      if (!Array.isArray(c.days) || !c.days.length || c.days.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) return `${what}: pick at least one day.`;
      if (!isHHMM(c.time)) return `${what}: enter a time.`;
    } else if (c.type === "once") {
      if (!num(c.at)) return `${what}: enter a date and time.`;
    } else if (c.type === "rule") {
      const e = rule(c, what);
      if (e) return e;
      if (c.also !== undefined && !Array.isArray(c.also)) return `${what}: unknown extra conditions.`;
      for (const [j, a] of (c.also ?? []).entries()) {
        const e2 = rule(a, `${what}, extra condition ${j + 1}`);
        if (e2) return e2;
      }
    } else return `${what}: unknown type.`;
  }
  const action = (a: Action, what: string) => {
    const e = board(a.devEui, what, a.port);
    if (e === "milesight") {
      if (typeof a.state !== "boolean") return `${what}: say whether to turn it on or off.`;
      if (!num(a.waitSec) || a.waitSec < 0 || a.waitSec > 86_400) return `${what}: the wait is 0 to 86400 seconds.`;
      return null;
    }
    if (e) return e;
    if (!["do", "ro", "ao"].includes(a.kind) || !Number.isInteger(a.channel) || a.channel < 1 || a.channel > 8) return `${what}: choose an output.`;
    if (a.kind === "ao" ? !num(a.value) : typeof a.state !== "boolean") return `${what}: say what to set it to.`;
    if (!num(a.waitSec) || a.waitSec < 0 || a.waitSec > 86_400) return `${what}: the wait is 0 to 86400 seconds.`;
    return null;
  };
  for (const [i, a] of p.startActions.entries()) {
    const e = action(a, `Start action ${i + 1}`);
    if (e) return e;
  }
  for (const [i, s] of p.steps.entries()) {
    const what = `Step ${i + 1}`;
    const e = board(s.devEui, what, s.port);
    if (e && e !== "milesight") return e;
    if (e !== "milesight" && (!["do", "ro"].includes(s.kind) || !Number.isInteger(s.channel) || s.channel < 1 || s.channel > 8)) return `${what}: choose a relay or digital output.`;
    if (!s.untilStop && (!num(s.runMin) || s.runMin <= 0 || s.runMin > 7 * 24 * 60)) return `${what}: run time is more than 0 and up to 7 days.`;
    if (!num(s.waitSec) || s.waitSec < 0 || s.waitSec > 7 * 86_400) return `${what}: it starts 0 to 7 days after the steps begin.`;
  }
  // Two steps on one output at the same time: switching the first off would cut the second short.
  for (const [i, a] of p.steps.entries()) {
    for (const [j, b] of p.steps.entries()) {
      if (j <= i || a.devEui !== b.devEui || outKey(a) !== outKey(b)) continue;
      const aEnd = a.untilStop ? Infinity : a.waitSec + a.runMin * 60;
      const bEnd = b.untilStop ? Infinity : b.waitSec + b.runMin * 60;
      if (a.waitSec < bEnd && b.waitSec < aEnd) return `Steps ${i + 1} and ${j + 1} switch the same output at the same time. Start one after the other ends.`;
    }
  }
  for (const [i, c] of p.stopConditions.entries()) {
    const e = rule(c, `Stop condition ${i + 1}`);
    if (e) return e;
    if (typeof c.pause !== "boolean") return `Stop condition ${i + 1}: choose pause or stop.`;
  }
  for (const [i, a] of p.stopActions.entries()) {
    const e = action(a, `Stop action ${i + 1}`);
    if (e) return e;
  }
  return null;
}
